import { useState, useRef, useCallback, useEffect } from 'react';
import { MicVAD } from '@ricky0123/vad-web';

export interface AppError {
  message: string;
  type: 'network' | 'permission' | 'audio' | 'api' | 'unknown';
  retryable: boolean;
}

export function useAudioRecording() {
  const [isRecording, setIsRecording] = useState<boolean>(false);
  const [isListening, setIsListening] = useState<boolean>(false);
  const [error, setError] = useState<AppError | null>(null);
  
  const vadRef = useRef<MicVAD | null>(null);
  const onRecordingCompleteRef = useRef<((audioData: string, mimeType: string) => void) | null>(null);
  const onSpeechStartRef = useRef<(() => void) | null>(null);
  const recordedAudioRef = useRef<Float32Array[]>([]);
  const isRecordingRef = useRef<boolean>(false);

  // Convert Float32Array audio to base64
  const audioToBase64 = useCallback((audioData: Float32Array, sampleRate: number = 16000): string => {
    try {
      // Convert Float32Array to 16-bit PCM
      const buffer = new ArrayBuffer(audioData.length * 2);
      const view = new DataView(buffer);
      
      for (let i = 0; i < audioData.length; i++) {
        // Convert float to 16-bit signed integer
        const sample = Math.max(-1, Math.min(1, audioData[i]));
        view.setInt16(i * 2, sample * 0x7FFF, true);
      }
      
      // Create WAV header
      const wavHeader = new ArrayBuffer(44);
      const wavView = new DataView(wavHeader);
      
      // WAV header
      const writeString = (offset: number, string: string) => {
        for (let i = 0; i < string.length; i++) {
          wavView.setUint8(offset + i, string.charCodeAt(i));
        }
      };
      
      writeString(0, 'RIFF');
      wavView.setUint32(4, 36 + buffer.byteLength, true);
      writeString(8, 'WAVE');
      writeString(12, 'fmt ');
      wavView.setUint32(16, 16, true);
      wavView.setUint16(20, 1, true);
      wavView.setUint16(22, 1, true); // mono
      wavView.setUint32(24, sampleRate, true);
      wavView.setUint32(28, sampleRate * 2, true);
      wavView.setUint16(32, 2, true);
      wavView.setUint16(34, 16, true);
      writeString(36, 'data');
      wavView.setUint32(40, buffer.byteLength, true);
      
      // Combine header and data
      const fullBuffer = new Uint8Array(wavHeader.byteLength + buffer.byteLength);
      fullBuffer.set(new Uint8Array(wavHeader), 0);
      fullBuffer.set(new Uint8Array(buffer), wavHeader.byteLength);
      
      // Convert to base64
      let binary = '';
      for (let i = 0; i < fullBuffer.byteLength; i++) {
        binary += String.fromCharCode(fullBuffer[i]);
      }
      
      return btoa(binary);
    } catch (error) {
      console.error('Error converting audio to base64:', error);
      throw new Error('Failed to convert audio to base64');
    }
  }, []);

  // Initialize VAD
  const initializeVAD = useCallback(async (): Promise<void> => {
    try {
      if (vadRef.current) {
        await vadRef.current.destroy();
        vadRef.current = null;
      }

      console.log('Initializing VAD');
      // Check microphone permissions first
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        } });
        stream.getTracks().forEach(track => track.stop()); // Stop the test stream
      } catch (permError) {
        console.error('Microphone permission denied:', permError);
        throw new Error('Microphone access required for voice detection');
      }
      
      const vad = await MicVAD.new({
        // Ultra-sensitive VAD settings for maximum detection
        positiveSpeechThreshold: 0.5,    // standard threshold for speech detection
        negativeSpeechThreshold: 0.5,    // standard threshold for silence detection  
        minSpeechFrames: 1,               // standard frames required to detect speech
        preSpeechPadFrames: 1,            // standard padding before speech
        redemptionFrames: 5,              // standard frames to continue recording after silence
        frameSamples: 1536,               // Frame size
        
        // Event handlers
        onSpeechStart: () => {
          if (onSpeechStartRef.current) {
            onSpeechStartRef.current();
          }
          if (!isRecordingRef.current) {
            setIsRecording(true);
            isRecordingRef.current = true;
            recordedAudioRef.current = [];
          }
        },
        
        onSpeechEnd: (audio: Float32Array) => {
          if (isRecordingRef.current && audio && audio.length > 0) {
            setIsRecording(false);
            isRecordingRef.current = false;
            
            try {
              const base64Audio = audioToBase64(audio);
              const mimeType = 'audio/wav';
              
              if (onRecordingCompleteRef.current) {
                onRecordingCompleteRef.current(base64Audio, mimeType);
              }
            } catch (error) {
              console.error('Error processing recorded audio:', error);
              setError({
                message: 'Failed to process recorded audio',
                type: 'audio',
                retryable: true
              });
            }
          }
        },
        
        onVADMisfire: () => {
          if (isRecordingRef.current) {
            setIsRecording(false);
            isRecordingRef.current = false;
            recordedAudioRef.current = [];
          }
        }
      });

      vadRef.current = vad;
      
    } catch (error) {
      console.error('Failed to initialize VAD:', error);
      setError({
        message: 'Failed to initialize voice detection. Please check your browser compatibility and ensure microphone permissions are granted.',
        type: 'audio',
        retryable: true
      });
      throw error;
    }
  }, [audioToBase64]);

  // Start listening for voice input
  const startRecording = useCallback(async (
    onRecordingComplete: (audioData: string, mimeType: string) => void,
    onSpeechStart?: () => void
  ): Promise<void> => {
    try {
      setError(null);
      onRecordingCompleteRef.current = onRecordingComplete;
      onSpeechStartRef.current = onSpeechStart || null;
      
      if (!vadRef.current) {
        await initializeVAD();
      }
      
      if (vadRef.current) {
        await vadRef.current.start();
        setIsListening(true);
      } else {
        throw new Error('VAD instance not available');
      }
    } catch (error) {
      console.error('Failed to start VAD listening:', error);
      setError({
        message: 'Failed to start voice detection',
        type: 'audio',
        retryable: true
      });
      throw error;
    }
  }, [initializeVAD]);

  // Stop listening
  const stopRecording = useCallback(async (): Promise<void> => {
    try {
      if (vadRef.current) {
        vadRef.current.pause();
        setIsListening(false);
        setIsRecording(false);
        isRecordingRef.current = false;
        recordedAudioRef.current = [];
      }
      onRecordingCompleteRef.current = null;
      onSpeechStartRef.current = null;
    } catch (error) {
      console.error('Error stopping VAD:', error);
    }
  }, []);

  // Update the recording callback while recording is active
  const updateCallbacks = useCallback((
    onRecordingComplete: (audioData: string, mimeType: string) => void,
    onSpeechStart?: () => void
  ): void => {
    onRecordingCompleteRef.current = onRecordingComplete;
    onSpeechStartRef.current = onSpeechStart || null;
  }, []);

  // Add this function to allow dynamic VAD sensitivity adjustment
  const setVADSensitivity = useCallback((isSensitive: boolean) => {
    if (vadRef.current && typeof vadRef.current.setOptions === 'function') {
      const params = {
        // A lower threshold is more sensitive. `isSensitive: true` should mean lower threshold.
        positiveSpeechThreshold: isSensitive ? 0.5 : 0.8,    // 0.5 is standard, 0.8 is less sensitive
        negativeSpeechThreshold: isSensitive ? 0.35 : 0.5,  // lower value for more sensitivity
        minSpeechFrames: isSensitive ? 1 : 3,               // fewer frames for more sensitivity
      };
      vadRef.current.setOptions(params);
      console.log(`VAD sensitivity adjusted. Sensitive: ${isSensitive}`, params);
    } else {
      // If setOptions is not available, log a warning
      console.warn('VAD setOptions not available; cannot adjust sensitivity dynamically.');
    }
  }, []);

  // Cleanup VAD on unmount
  useEffect(() => {
    return () => {
      if (vadRef.current) {
        try {
          vadRef.current.destroy();
          vadRef.current = null;
        } catch (error) {
          console.error('Error cleaning up VAD:', error);
        }
      }
    };
  }, []);

  return {
    isRecording,
    isListening,
    error,
    startRecording,
    stopRecording,
    updateCallbacks,
    setVADSensitivity
  };
} 